// Lista-sor akció-gombok — EGYSÉGES minta minden lista-oldalon (a Clients-oldal a referencia).
// KÉT megjelenítési kontextus, EGY API (a hívó ugyanazt a `RowActions`/`RowActionButton`-fát adja meg):
//   • `toolbar` (default, desktop-tábla utolsó oszlopa): ghost + icon gomb, `h-4 w-4` ikon, TOOLTIP a címkével.
//   • `menu` (mobil ⋯ overflow-menü, a DataTable adja a kontextuson át): ikon + SZÖVEGES `DropdownMenuItem`,
//     tooltip NÉLKÜL (a felirat kint van), a törlés `text-destructive`. A tooltip így KIZÁRÓLAG desktopon van.
// A jog-vezérlést (can(...)) a hívó dönti el (mit renderel); ez a réteg CSAK a kinézetet egységesíti.
import { EyeIcon, PencilSquareIcon, TrashIcon } from '@heroicons/react/24/outline';
import {
  createContext,
  useContext,
  type ComponentType,
  type ReactNode,
  type SVGProps,
} from 'react';

import { Button } from '@/components/ui/button';
import { DropdownMenuItem } from '@/components/ui/dropdown-menu';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';

/** A sor-akciók megjelenítési kontextusa. A DataTable a mobil ⋯-menü tartalmát `menu`-ra állítja; máshol `toolbar`. */
export type RowActionsVariant = 'toolbar' | 'menu';
export const RowActionsVariantContext = createContext<RowActionsVariant>('toolbar');

/** A sor-akciók konténere. `toolbar` → jobbra igazított, egysoros ikon-sor; `menu` → átlátszó fragment
 *  (a `DropdownMenuContent` rendezi a menüpontokat egymás alá). */
export function RowActions({ children }: { children: ReactNode }): JSX.Element {
  const variant = useContext(RowActionsVariantContext);
  if (variant === 'menu') return <>{children}</>;
  return <div className="flex items-center justify-end gap-1">{children}</div>;
}

type IconType = ComponentType<SVGProps<SVGSVGElement>>;

interface RowActionButtonProps {
  /** aria-label ÉS tooltip-szöveg (toolbar) / a menüpont felirata (menu) — i18n-kulcsból. */
  label: string;
  onClick: () => void;
  icon: IconType;
  /** Destruktív (törlés) → az ikon (toolbar) / a menüpont (menu) `text-destructive`. */
  destructive?: boolean;
  disabled?: boolean;
  /** Ha disabled (toolbar): a tooltip ezt mutatja. Menüben a menüpont egyszerűen tiltott. */
  disabledTooltip?: string;
}

/**
 * Egyetlen sor-akció — a lista-oldalak MINDEN akciója (szerkesztés/törlés/megtekintés + „egyéb": jóváhagyás,
 * küldés, időzítés, …) EZT használja, hogy a kinézet mindenhol azonos legyen. A megjelenés a kontextustól függ:
 * `toolbar` → ghost/icon gomb + Tooltip; `menu` → ikon + szöveges `DropdownMenuItem` (tooltip nélkül).
 */
export function RowActionButton({
  label,
  onClick,
  icon: Icon,
  destructive = false,
  disabled = false,
  disabledTooltip,
}: RowActionButtonProps): JSX.Element {
  const variant = useContext(RowActionsVariantContext);

  // MENÜ (mobil ⋯): ikon + szöveges felirat, tooltip NÉLKÜL. A `DropdownMenuItem` a záródást + a billentyű-
  // navigációt kezeli; a `onSelect` a click ekvivalense (a menü-wrapper stopPropagation-je védi a kártya-onRowClick-et).
  if (variant === 'menu') {
    return (
      <DropdownMenuItem
        disabled={disabled}
        onSelect={() => {
          if (!disabled) onClick();
        }}
        className={destructive ? 'text-destructive focus:text-destructive' : undefined}
        title={disabled ? (disabledTooltip ?? label) : undefined}
      >
        <Icon className="h-4 w-4 shrink-0" />
        <span>{label}</span>
      </DropdownMenuItem>
    );
  }

  // TOOLBAR (desktop-tábla): ghost/icon gomb + Tooltip a címkével.
  const button = (
    <Button
      variant="ghost"
      size="icon"
      aria-label={label}
      disabled={disabled}
      onClick={disabled ? undefined : onClick}
    >
      <Icon className={destructive ? 'text-destructive h-4 w-4' : 'h-4 w-4'} />
    </Button>
  );
  return (
    <Tooltip>
      {/* disabled gomb nem tüzel pointer-eventet → a tooltip egy fókuszálható span-en ül. */}
      <TooltipTrigger asChild>
        {disabled ? <span tabIndex={0}>{button}</span> : button}
      </TooltipTrigger>
      <TooltipContent>{disabled ? (disabledTooltip ?? label) : label}</TooltipContent>
    </Tooltip>
  );
}

/** Szerkesztés (ceruza) sor-akció. */
export function EditRowAction(props: { label: string; onClick: () => void }): JSX.Element {
  return <RowActionButton label={props.label} onClick={props.onClick} icon={PencilSquareIcon} />;
}

/** Megtekintés (szem) sor-akció — jog hiányában a szerkesztés helyett. */
export function ViewRowAction(props: { label: string; onClick: () => void }): JSX.Element {
  return <RowActionButton label={props.label} onClick={props.onClick} icon={EyeIcon} />;
}

/** Törlés (kuka) sor-akció — destruktív tónus. */
export function DeleteRowAction(props: { label: string; onClick: () => void }): JSX.Element {
  return (
    <RowActionButton label={props.label} onClick={props.onClick} icon={TrashIcon} destructive />
  );
}
